import { cva, type VariantProps } from "class-variance-authority";
import type * as React from "react";

import { cn } from "@/lib/utils";

const typographyVariants = cva("", {
  variants: {
    variant: {
      // Per Figma LUARY-DS2: each --text-{size} carries its own weight +
      // letter-spacing as a default (e.g. text-4xl auto-applies Bold 700
      // + tracking-tighter). Variants here only add what the size-default
      // can't express. h4 keeps explicit font-semibold + tracking-tight
      // because text-xl's defaults are Medium / tracking-normal — a
      // deliberate heading override.
      h1: "font-heading scroll-m-20 text-4xl text-balance",
      h2: "font-heading scroll-m-20 border-b pb-2 text-3xl first:mt-0",
      h3: "font-heading scroll-m-20 text-2xl",
      h4: "font-heading scroll-m-20 text-xl font-semibold tracking-tight",
      p: "leading-relaxed [&:not(:first-child)]:mt-6",
      lead: "text-muted-foreground text-xl",
      large: "text-lg font-semibold",
      small: "text-sm leading-none",
      muted: "text-muted-foreground text-sm",
      blockquote: "mt-6 border-l-2 pl-6 italic",
      code: "bg-muted relative rounded px-[0.3rem] py-[0.2rem] font-mono text-sm font-semibold",
      list: "my-6 ml-6 list-disc [&>li]:mt-2",
    },
  },
  defaultVariants: { variant: "p" },
});

type Variant = NonNullable<VariantProps<typeof typographyVariants>["variant"]>;

const defaultElement: Record<Variant, React.ElementType> = {
  h1: "h1",
  h2: "h2",
  h3: "h3",
  h4: "h4",
  p: "p",
  lead: "p",
  large: "div",
  small: "small",
  muted: "p",
  blockquote: "blockquote",
  code: "code",
  list: "ul",
};

type TypographyProps = {
  variant?: Variant;
  as?: React.ElementType;
  className?: string;
  children?: React.ReactNode;
} & Omit<React.HTMLAttributes<HTMLElement>, "className">;

function Typography({
  variant = "p",
  as,
  className,
  ...props
}: TypographyProps) {
  const Element = as ?? defaultElement[variant];
  return (
    <Element
      data-slot="typography"
      data-variant={variant}
      className={cn(typographyVariants({ variant }), className)}
      {...props}
    />
  );
}

function H1({ className, ...props }: React.ComponentProps<"h1">) {
  return (
    <h1
      data-slot="typography"
      data-variant="h1"
      className={cn(typographyVariants({ variant: "h1" }), className)}
      {...props}
    />
  );
}

function H2({ className, ...props }: React.ComponentProps<"h2">) {
  return (
    <h2
      data-slot="typography"
      data-variant="h2"
      className={cn(typographyVariants({ variant: "h2" }), className)}
      {...props}
    />
  );
}

function H3({ className, ...props }: React.ComponentProps<"h3">) {
  return (
    <h3
      data-slot="typography"
      data-variant="h3"
      className={cn(typographyVariants({ variant: "h3" }), className)}
      {...props}
    />
  );
}

function H4({ className, ...props }: React.ComponentProps<"h4">) {
  return (
    <h4
      data-slot="typography"
      data-variant="h4"
      className={cn(typographyVariants({ variant: "h4" }), className)}
      {...props}
    />
  );
}

function P({ className, ...props }: React.ComponentProps<"p">) {
  return (
    <p
      data-slot="typography"
      data-variant="p"
      className={cn(typographyVariants({ variant: "p" }), className)}
      {...props}
    />
  );
}

function Lead({ className, ...props }: React.ComponentProps<"p">) {
  return (
    <p
      data-slot="typography"
      data-variant="lead"
      className={cn(typographyVariants({ variant: "lead" }), className)}
      {...props}
    />
  );
}

function Large({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="typography"
      data-variant="large"
      className={cn(typographyVariants({ variant: "large" }), className)}
      {...props}
    />
  );
}

function Small({ className, ...props }: React.ComponentProps<"small">) {
  return (
    <small
      data-slot="typography"
      data-variant="small"
      className={cn(typographyVariants({ variant: "small" }), className)}
      {...props}
    />
  );
}

function Muted({ className, ...props }: React.ComponentProps<"p">) {
  return (
    <p
      data-slot="typography"
      data-variant="muted"
      className={cn(typographyVariants({ variant: "muted" }), className)}
      {...props}
    />
  );
}

function Blockquote({
  className,
  ...props
}: React.ComponentProps<"blockquote">) {
  return (
    <blockquote
      data-slot="typography"
      data-variant="blockquote"
      className={cn(typographyVariants({ variant: "blockquote" }), className)}
      {...props}
    />
  );
}

function Code({ className, ...props }: React.ComponentProps<"code">) {
  return (
    <code
      data-slot="typography"
      data-variant="code"
      className={cn(typographyVariants({ variant: "code" }), className)}
      {...props}
    />
  );
}

function List({ className, ...props }: React.ComponentProps<"ul">) {
  return (
    <ul
      data-slot="typography"
      data-variant="list"
      className={cn(typographyVariants({ variant: "list" }), className)}
      {...props}
    />
  );
}

export {
  Blockquote,
  Code,
  H1,
  H2,
  H3,
  H4,
  Large,
  Lead,
  List,
  Muted,
  P,
  Small,
  Typography,
  typographyVariants,
};
